import React, { useEffect, useMemo, useState } from "react";

import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/es";
import "dayjs/locale/pt";
import "dayjs/locale/pt-br";

import { BaseBadge, BaseLabel, colors } from "components";

import { useTranslation } from "hooks/useTranslation";

import * as S from "./styles";

export interface IDateInput {
  id?: string;
  label: string;
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  width?: number;
  value?: string | null;
  setValue: (e: string | null) => void;
  clear?: number;
  minDate?: string | null;
  maxDate?: string | null;
}

export const DateInput: React.FC<IDateInput> = ({
  id,
  label,
  disabled,
  error = false,
  required = false,
  width,
  value,
  setValue,
  clear,
  minDate,
  maxDate,
}) => {
  const { dayjsLocale } = useTranslation();
  const [internalValue, setInternalValue] = useState<Dayjs | null>(null);
  const minDateValue = useMemo(() => {
    if (!minDate) return undefined;

    const date = dayjs(minDate);
    return date.isValid() ? date : undefined;
  }, [minDate]);
  const maxDateValue = useMemo(() => {
    if (!maxDate) return undefined;

    const date = dayjs(maxDate);
    return date.isValid() ? date : undefined;
  }, [maxDate]);

  useEffect(() => {
    if (value === null || value === "" || value === undefined) {
      setInternalValue(null);
    } else {
      setInternalValue(dayjs(value));
    }
  }, [value]);

  useEffect(() => {
    if (clear && clear > 0) {
      handleClearDate();
    }
  }, [clear]);

  const handleChangeDate = (newValue: Dayjs | null) => {
    setInternalValue(newValue);

    const data = dayjs(newValue);
    const dataFormat = data.format("YYYY-MM-DD");
    setValue(dataFormat);
  };

  const handleClearDate = () => {
    setInternalValue(null);
    setValue(null);
  };

  return (
    <S.Container id={id} data-testid={id}>
      <LocalizationProvider
        dateAdapter={AdapterDayjs}
        adapterLocale={dayjsLocale}
      >
        <S.ContainerHeader>
          <BaseLabel m="none" marginRight="8px" color={colors.neutral_gray_40}>
            {label}
          </BaseLabel>
          <S.LabelRequired>
            {required ? <BaseBadge color="warning" /> : <></>}
          </S.LabelRequired>
        </S.ContainerHeader>
        <DatePicker
          format="D/M/YYYY"
          disabled={disabled}
          className={error === true ? "input_mui_error" : "input_mui"}
          dayOfWeekFormatter={(day) => day.format("ddd")}
          slotProps={{
            field: { clearable: !required, onClear: () => handleClearDate },
          }}
          sx={{ width: width || "100%" }}
          value={internalValue}
          minDate={minDateValue}
          maxDate={maxDateValue}
          onChange={(newValue) => handleChangeDate(newValue)}
        />
      </LocalizationProvider>
    </S.Container>
  );
};

export default DateInput;
